import { _defaults } from './defaults';
import { cleanUrl, escape } from './helpers';
import type { MarkedOptions } from './MarkedOptions';
import type { _Parser } from './Parser';
import { other } from './rules';
import {
  Blockquote,
  Br,
  Checkbox,
  Code,
  Codespan,
  Def,
  Del,
  Em,
  Escape,
  Heading,
  Hr,
  HTML,
  Image,
  Link,
  List,
  ListItem,
  Paragraph,
  Space,
  Strong,
  Table,
  TableCell,
  TableRow,
  Tag,
  Text,
} from './Tokens';

/**
 * Renderer
 */
export class _Renderer<ParserOutput = string, RendererOutput = string> {
  options: MarkedOptions<ParserOutput, RendererOutput>;
  parser!: _Parser<ParserOutput, RendererOutput>; // set by the parser
  constructor(options?: MarkedOptions<ParserOutput, RendererOutput>) {
    this.options = options || _defaults;
  }

  space(token: Space): RendererOutput {
    return '' as RendererOutput;
  }

  code({ text, lang, escaped }: Code): RendererOutput {
    const langString = (lang || '').match(other.notSpaceStart)?.[0];

    const code = text.replace(other.endingNewline, '') + '\n';

    if (!langString) {
      return ('<pre><code>' + (escaped ? code : escape(code, true)) + '</code></pre>\n') as RendererOutput;
    }

    return ('<pre><code class="language-' +
      escape(langString) +
      '">' +
      (escaped ? code : escape(code, true)) +
      '</code></pre>\n') as RendererOutput;
  }

  blockquote({ tokens }: Blockquote): RendererOutput {
    const body = this.parser.parse(tokens);
    return `<blockquote>\n${body}</blockquote>\n` as RendererOutput;
  }

  html({ text }: HTML | Tag): RendererOutput {
    return text as RendererOutput;
  }

  def(token: Def): RendererOutput {
    return '' as RendererOutput;
  }

  heading({ tokens, depth }: Heading): RendererOutput {
    return `<h${depth}>${this.parser.parseInline(tokens)}</h${depth}>\n` as RendererOutput;
  }

  hr(token: Hr): RendererOutput {
    return '<hr>\n' as RendererOutput;
  }

  list(token: List): RendererOutput {
    const ordered = token.ordered;
    const start = token.start;

    let body = '';
    for (let j = 0; j < token.items.length; j++) {
      const item = token.items[j];
      body += this.listitem(item);
    }

    const type = ordered ? 'ol' : 'ul';
    const startAttr = ordered && start !== 1 ? ' start="' + start + '"' : '';
    return ('<' + type + startAttr + '>\n' + body + '</' + type + '>\n') as RendererOutput;
  }

  listitem(item: ListItem): RendererOutput {
    let itemBody = '';
    if (item.task) {
      const checkbox = this.checkbox({ checked: !!item.checked });
      if (item.loose) {
        if (item.tokens[0]?.type === 'paragraph') {
          item.tokens[0].text = checkbox + ' ' + item.tokens[0].text;
          if (item.tokens[0].tokens && item.tokens[0].tokens.length > 0 && item.tokens[0].tokens[0].type === 'text') {
            item.tokens[0].tokens[0].text = checkbox + ' ' + escape(item.tokens[0].tokens[0].text);
            item.tokens[0].tokens[0].escaped = true;
          }
        } else {
          item.tokens.unshift({
            type: 'text',
            raw: checkbox + ' ',
            text: checkbox + ' ',
            escaped: true,
          });
        }
      } else {
        itemBody += checkbox + ' ';
      }
    }

    itemBody += this.parser.parse(item.tokens, !!item.loose);

    return `<li>${itemBody}</li>\n` as RendererOutput;
  }

  checkbox({ checked }: Checkbox): RendererOutput {
    return ('<input ' + (checked ? 'checked="" ' : '') + 'disabled="" type="checkbox">') as RendererOutput;
  }

  paragraph({ tokens }: Paragraph): RendererOutput {
    return `<p>${this.parser.parseInline(tokens)}</p>\n` as RendererOutput;
  }

  table(token: Table): RendererOutput {
    let header = '';

    // header
    let cell = '';
    for (let j = 0; j < token.header.length; j++) {
      cell += this.tablecell(token.header[j]);
    }
    header += this.tablerow({ text: cell as ParserOutput });

    let body = '';
    for (let j = 0; j < token.rows.length; j++) {
      const row = token.rows[j];

      cell = '';
      for (let k = 0; k < row.length; k++) {
        cell += this.tablecell(row[k]);
      }

      body += this.tablerow({ text: cell as ParserOutput });
    }
    if (body) body = `<tbody>${body}</tbody>`;

    return ('<table>\n' + '<thead>\n' + header + '</thead>\n' + body + '</table>\n') as RendererOutput;
  }

  tablerow({ text }: TableRow<ParserOutput>): RendererOutput {
    return `<tr>\n${text}</tr>\n` as RendererOutput;
  }

  tablecell(token: TableCell): RendererOutput {
    const content = this.parser.parseInline(token.tokens);
    const type = token.header ? 'th' : 'td';
    const tag = token.align ? `<${type} align="${token.align}">` : `<${type}>`;
    return (tag + content + `</${type}>\n`) as RendererOutput;
  }

  /**
   * span level renderer
   */
  strong({ tokens }: Strong): RendererOutput {
    return `<strong>${this.parser.parseInline(tokens)}</strong>` as RendererOutput;
  }

  em({ tokens }: Em): RendererOutput {
    return `<em>${this.parser.parseInline(tokens)}</em>` as RendererOutput;
  }

  codespan({ text }: Codespan): RendererOutput {
    return `<code>${escape(text, true)}</code>` as RendererOutput;
  }

  br(token: Br): RendererOutput {
    return '<br>' as RendererOutput;
  }

  del({ tokens }: Del): RendererOutput {
    return `<del>${this.parser.parseInline(tokens)}</del>` as RendererOutput;
  }

  link({ href, title, tokens }: Link): RendererOutput {
    const text = this.parser.parseInline(tokens) as string;
    const cleanHref = cleanUrl(href);
    if (cleanHref === null) {
      return text as RendererOutput;
    }
    href = cleanHref;
    let out = '<a href="' + href + '"';
    if (title) {
      out += ' title="' + escape(title) + '"';
    }
    out += '>' + text + '</a>';
    return out as RendererOutput;
  }

  image({ href, title, text, tokens }: Image): RendererOutput {
    if (tokens) {
      text = this.parser.parseInline(tokens, this.parser.textRenderer) as string;
    }
    const cleanHref = cleanUrl(href);
    if (cleanHref === null) {
      return escape(text) as RendererOutput;
    }
    href = cleanHref;

    let out = `<img src="${href}" alt="${text}"`;
    if (title) {
      out += ` title="${escape(title)}"`;
    }
    out += '>';
    return out as RendererOutput;
  }

  text(token: Text | Escape): RendererOutput {
    return 'tokens' in token && token.tokens
      ? (this.parser.parseInline(token.tokens) as unknown as RendererOutput)
      : 'escaped' in token && token.escaped
      ? (token.text as RendererOutput)
      : (escape(token.text) as RendererOutput);
  }
}
